import { rem } from 'polished'
import { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import WalledContent from 'components/Common/WalledContent'
import FormatDateTimeRange from 'components/utils/Formatters/FormatDateTimeRange'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useOfferPageWalledGarden from 'hooks/OfferPage/useOfferPageWalledGarden'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { getPropertyCheapestPromotionRates } from 'selectors/offerPage/offerPageSelectors'
import { isSppMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'

import PropertyCancellationPolicy from './PropertyCancellationPolicy'
import PropertyCheapestRatePriceDetails from './PropertyCheapestRatePriceDetails'
import PropertyPromotionLabel from './PropertyPromotionLabel'
import PropertyRateInclusions from './PropertyRateInclusions'
import { isRestrictedPropertyPolicyEligibleRate } from 'lib/offer/cancellationPolicyUtils'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${rem(36)} !important;

  > * + * {
    margin-top: ${rem(20)};
  }
`

export const DetailSegment = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  position: relative;
  padding: ${rem(20)} ${rem(24)};
  display: flex;
  flex-direction: column;
  gap: ${rem(16)} ${rem(20)};

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    min-height: ${rem(160)};
  }
`

const LeftDetailSegment = styled.div`
  > * + * {
    margin-top: ${rem(16)}
  }

  ${mediaQueryUp.desktop} {
    flex: 1 0 0;
    align-self: flex-start;
  }
`

const RightDetailSegment = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  margin-top: ${rem(16)};
  position: relative;
  width: auto;
  min-width: ${rem(180)};
  gap: ${rem(16)};

  ${mediaQueryUp.desktop} {
    align-self: flex-end;
    flex: 0 0 40%;
  }
`
const TravelPeriodSection = styled.div`
  display: flex;
  flex-direction: column;
`

interface Props {
  property: App.Property.Property
  cheapestPromotionPropertyRates: [App.EnrichedPromotionPropertyRate | App.Property.AnytimePromotionRate | undefined, App.EnrichedPromotionPropertyRate | undefined]
  multipleRoomBookingActive: boolean
}

function PropertyPromotionSummary({
  property,
  cheapestPromotionPropertyRates,
  multipleRoomBookingActive,
}: Props) {
  const dispatch = useOfferPageDispatch()
  const intl = useIntl()
  const showWall = useOfferPageWalledGarden()

  const [hotelOnlyRate, hotelWithFlightsRate] = cheapestPromotionPropertyRates
  const promotionStartDate = hotelOnlyRate?.promotion.travelFromIsoDate

  const onCheckAvailability = useCallback(() => {
    // We still need to sort out analytics for this CTA.
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin', calendarStartDate: promotionStartDate })
  }, [dispatch, promotionStartDate])

  if (!hotelOnlyRate) return null

  const promotion = hotelOnlyRate.promotion
  const hotelOnlyRateHeadlineCancellationPolicyStart =
    'headlineCancellationPolicyStart' in hotelOnlyRate && hotelOnlyRate.headlineCancellationPolicyStart
      ? new Date(hotelOnlyRate.headlineCancellationPolicyStart)
      : undefined
  const {
    inclusions,
    bonusInclusions,
    luxPlusInclusions,
    bundleInclusions,
  } = hotelOnlyRate
  const hasInclusions = !!(inclusions.length || bonusInclusions.length || luxPlusInclusions.length || bundleInclusions.length)
  const isWalledGardenEnforced = property.walledGarden && showWall

  return <Container>
    <div>
      <Heading variant="heading4">
        <FormattedMessage
          id="v9AH4RKuXooJCX8_Ctjw"
          defaultMessage="Current promotions"
          description="Section heading for current promotions on single property page"
        />
      </Heading>
      <BodyText variant="medium" as="p">
        <FormattedMessage
          id="bVwH-uH996H6vrq4stWg"
          defaultMessage="Available for select rooms only"
          description="Subtitle for current promotions section on single property page"
        />
      </BodyText>
    </div>
    <DetailSegment>
      <LeftDetailSegment>
        <PropertyPromotionLabel promotion={promotion} />
        <Heading variant="heading5">{promotion.name}</Heading>
        {'cancellationPolicies' in hotelOnlyRate && <PropertyCancellationPolicy
          propertyTimeZone={property.location.timezone}
          headlineCancellationPolicyType={hotelOnlyRate.headlineCancellationPolicyType}
          restrictedPolicyEligible={isRestrictedPropertyPolicyEligibleRate(hotelOnlyRate)}
          headlineCancellationPolicyStart={hotelOnlyRateHeadlineCancellationPolicyStart}
          fullyRefundableUntil={hotelOnlyRate.fullyRefundableUntil}
          cancellationPolicies={hotelOnlyRate.cancellationPolicies}
          currency={hotelOnlyRate.currency}
          shouldClarifyRefundIsHotelOnly={!!hotelWithFlightsRate}
        />}
        {hasInclusions && <VerticalSpacer gap={4}>
          <BodyText variant="medium" weight="bold" colour="light-foreground-alt">
            {intl.formatMessage({
              id: 'hfst-e9cKZKywWfECckb',
              defaultMessage: 'Special inclusions for select packages only',
              description: 'Heading for promotional rate inclusions section on single property page',
            })}
          </BodyText>
          <PropertyRateInclusions
            property={property}
            inclusions={inclusions}
            bonusInclusions={bonusInclusions}
            luxPlusInclusions={luxPlusInclusions}
            bundleInclusions={bundleInclusions}
            maxHeight={160}
          />
        </VerticalSpacer>}
        <TravelPeriodSection>
          <BodyText variant="small" weight="normal" colour="light-foreground-alt">
            <FormattedMessage
              id="HJlLCaOURo1Ds2nZ0rYi"
              defaultMessage="Travel between: {dateRange}. Minimum stay: {nights, plural, one {# night} other {# nights}}."
              description="Travel date range and minimum stay requirement for promotional rate"
              values={{
                dateRange: <FormatDateTimeRange startValue={promotion.travelFromIsoDate} endValue={promotion.travelToIsoDate} format="dmy-casual" />,
                nights: promotion.minimumLos,
              }}
            />
          </BodyText>
        </TravelPeriodSection>
      </LeftDetailSegment>
      <RightDetailSegment>
        {!multipleRoomBookingActive && <WalledContent enforced={isWalledGardenEnforced}>
          <PropertyCheapestRatePriceDetails
            hotelOnlyRate={hotelOnlyRate}
            hotelWithFlightsRate={
              // This is a quick heuristic to ensure the cheapest hotel-with-flights rate has the
              // same promotion as the cheapest hotel-only rate.
              'rateId' in hotelOnlyRate && hotelWithFlightsRate?.rateId === hotelOnlyRate.rateId
                ? hotelWithFlightsRate
                : undefined
            }
            horizontalAlign="start"
          />
        </WalledContent>}
        <TextButton onClick={onCheckAvailability} kind="primary" fit="flex">
          {intl.formatMessage({
            id: 'Bk6q1lWms6D6A0wYDWwZ',
            defaultMessage: 'Check availability',
            description: 'CTA text to open calendar from current promotions section on single property page',
          })}
        </TextButton>
      </RightDetailSegment>
    </DetailSegment>
  </Container>
}

type MappedProps = Props & { open: boolean }

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  property: state.property!, // Guaranteed by `<PropertyContainer/>`.
  cheapestPromotionPropertyRates: getPropertyCheapestPromotionRates(state, appState),
  multipleRoomBookingActive: isSppMultipleRoomBookingActive(state, appState),
  open: !!state.activeFilter,
}), PropertyPromotionSummary, preventRenderWithOpenFilter)
